
import { supabase } from '@/lib/supabase';
import { MOODS } from '@/lib/constants';
import type { LucideIcon } from 'lucide-react';
import type { Database } from '@/types/supabase';

// Types (reused/moved from hooks, ideally shared in a types folder)
export interface Message {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    created_at?: string;
}

interface Mood {
    id: string;
    label: string;
    icon: LucideIcon;
    color: string;
    bg: string;
}

export interface Conversation {
    id: string;
    personaId: string;
    contextId?: string;
    mood: Mood;
    context: string;
    messages: Message[];
    updatedAt: number | string;
    collectionId?: string | null;
    tags?: { id: string; name: string; color: string }[];
    createdAt?: string;
}

type ConversationRowWithDetails = Database['public']['Tables']['conversations']['Row'] & {
    messages?: { id: string; role: 'user' | 'assistant' | 'system'; content: string; created_at: string }[];
    tags?: { tag: Database['public']['Tables']['insight_tags']['Row'] }[] | null;
};

export const conversationService = {
    async fetchAll(offset: number = 0, limit: number = 50): Promise<Conversation[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase
            .from('conversations')
            .select(`
                *,
                messages (
                  id,
                  role,
                  content,
                  created_at
                ),
                tags:conversation_tag_links(tag:insight_tags(*))
            `)
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false })
            .range(offset, offset + limit - 1);

        if (error) throw error;

        const rows = (data || []) as unknown as ConversationRowWithDetails[];

        return rows.map((c) => ({
            id: c.id,
            personaId: c.persona_id,
            contextId: c.context_id || undefined,
            mood: MOODS.find(m => m.id === c.mood) || MOODS[0],
            context: c.context || "",
            messages: (c.messages || []).sort((a, b) =>
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            ) as Message[],
            updatedAt: c.updated_at,
            collectionId: c.collection_id,
            tags: c.tags ? c.tags.map((t) => ({
                id: t.tag.id,
                name: t.tag.name || '',
                color: t.tag.color || 'slate'
            })) : [],
            createdAt: c.created_at,
        }));
    },

    async create(
        personaId: string, 
        moodId: string, 
        contextId?: string, 
        context?: string, 
        collectionId?: string | null, 
        tagIds?: string[]
    ): Promise<Conversation> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: convo, error } = await supabase.from('conversations').insert({
            user_id: user.id,
            persona_id: personaId,
            context_id: contextId || null,
            mood: moodId,
            context: context || '',
            collection_id: collectionId || null,
        }).select().single();

        if (error) throw error;

        if (tagIds && tagIds.length > 0) {
            await conversationService.updateTags(convo.id, tagIds);
        }

        return {
            id: convo.id,
            personaId: convo.persona_id,
            contextId: convo.context_id || undefined,
            mood: MOODS.find(m => m.id === moodId) || MOODS[0],
            context: convo.context || "",
            messages: [],
            updatedAt: convo.updated_at,
            collectionId: convo.collection_id,
            tags: [],
            createdAt: convo.created_at,
        };
    },

    async addMessage(conversationId: string, role: 'user' | 'assistant', content: string) {
        const { data, error } = await supabase.from('messages').insert({
            conversation_id: conversationId,
            role,
            content
        }).select().single();

        if (error) throw error;
        return data;
    },

    async update(id: string, updates: Partial<Conversation>, tagIds?: string[]): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const dbUpdates: Database['public']['Tables']['conversations']['Update'] = {};
        if (updates.contextId !== undefined) dbUpdates.context_id = updates.contextId || null;
        if (updates.context !== undefined) dbUpdates.context = updates.context;
        if (updates.mood) dbUpdates.mood = (updates.mood as unknown as { id: string }).id;
        if (updates.collectionId !== undefined) dbUpdates.collection_id = updates.collectionId;

        if (Object.keys(dbUpdates).length > 0) {
            const { error } = await supabase.from('conversations').update(dbUpdates).eq('id', id).eq('user_id', user.id);
            if (error) throw error;
        }

        if (tagIds !== undefined) {
            await conversationService.updateTags(id, tagIds);
        }
    },

    async updateTags(conversationId: string, tagIds: string[]): Promise<void> {
        await supabase.from('conversation_tag_links').delete().eq('conversation_id', conversationId);
        if (tagIds.length > 0) {
            const links = tagIds.map(tagId => ({ conversation_id: conversationId, tag_id: tagId }));
            await supabase.from('conversation_tag_links').insert(links);
        }
    },

    async delete(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('conversations').delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    }
};
